import { displayErrorToast } from "@endya-conformite/ui-kit";
import { userSession } from "@src/data/session/UserSession";
import { handleGatewayError } from "@src/data/utils/handleGatewayError";
import { AxiosResponse } from "axios";

const mimeTypes: Record<string, string> = {
  pdf: "application/pdf",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  bmp: "image/bmp",
  svg: "image/svg+xml",
  webp: "image/webp",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  txt: "text/plain",
  csv: "text/csv",
  zip: "application/zip",
};

const defaultOptions = {
  type: "application/octet-stream",
};

const getMimeType = (filename: string): string => {
  const ext = filename.toLowerCase().split(".").pop() || "";
  return mimeTypes[ext] || defaultOptions.type;
};

export function downloadFile(
  fileOrUrl: ArrayBuffer | Blob,
  filename: string,
  options?: typeof defaultOptions
): void;

export function downloadFile(
  fileOrUrl: string,
  options?: typeof defaultOptions
): void;

export function downloadFile(
  fileOrUrl: ArrayBuffer | Blob | string,
  filenameOrOptions?: string | typeof defaultOptions,
  providedOptions: typeof defaultOptions = defaultOptions
): void {
  let fileUrl: string;
  let filename: string;
  let options = providedOptions;

  if (typeof fileOrUrl === "string") {
    fileUrl = fileOrUrl;
    filename = fileOrUrl.split("/").pop() || "download";
    options = filenameOrOptions as typeof defaultOptions;
  } else {
    filename = filenameOrOptions as string;
    const mimeType = options?.type || getMimeType(filename);
    const fileToDownload = new Blob([fileOrUrl], { type: mimeType });
    fileUrl = URL.createObjectURL(fileToDownload);
  }

  const linkElement = document.createElement("a");
  linkElement.href = fileUrl;
  linkElement.download = filename;
  linkElement.style.display = "none";
  document.body.appendChild(linkElement);
  linkElement.click();
  document.body.removeChild(linkElement);

  if (typeof fileOrUrl !== "string") {
    URL.revokeObjectURL(fileUrl);
  }
}

export function viewFileInNewTab(file: ArrayBuffer, filename: string): void {
  const mimeType = getMimeType(filename);
  const blob = new Blob([file], { type: mimeType });
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank");
}

export type DownloadedFile = {
  file: ArrayBuffer;
  filename: string;
  contentType?: string;
};
const handleDownloadedFile = async (
  call: () => Promise<AxiosResponse<ArrayBuffer>>,
  filename: string
): Promise<DownloadedFile | null> => {
  try {
    const response = await call();
    const contentType = response.headers["content-type"] as string | undefined;
    return {
      file: response.data,
      filename,
      contentType,
    };
  } catch (error) {
    handleGatewayError({
      onResponse: (e) => {
        if (e.response.status === 403) {
          displayErrorToast("Vous n'avez pas accès à ce fichier");
        }
        displayErrorToast(
          "Une erreur est survenue lors du téléchargement du fichier"
        );
      },
      onUnhandled: () => {
        displayErrorToast(
          "Une erreur est survenue lors du téléchargement du fichier"
        );
      },
    })(error);
    return null;
  }
};

export const getDownloadedFile = async (
  siren: string,
  millesime: number,
  pathname: string,
  filename: string,
  idAdmin = false
): Promise<DownloadedFile | null> => {
  return handleDownloadedFile(
    () =>
      userSession.axiosInstance.get<ArrayBuffer>(
        `/documents/${siren}${
          idAdmin ? "" : `/user/${millesime}`
        }?pathname=${encodeURIComponent(pathname)}`,
        {
          responseType: "blob",
        }
      ),
    filename
  );
};
export const getDownloadedFileAdmin = async (
  siren: string,
  pathname: string,
  filename: string
): Promise<DownloadedFile | null> => {
  return handleDownloadedFile(
    () =>
      userSession.axiosInstance.get<ArrayBuffer>(
        `/documents/${siren}?pathname=${encodeURIComponent(pathname)}`,
        {
          responseType: "blob",
        }
      ),
    filename
  );
};
